/**
 * Terms of Service Modal Component
 * Displays the full terms and conditions in a scrollable modal dialog
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'

interface TermsOfServiceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TermsOfServiceModal({ open, onOpenChange }: TermsOfServiceModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[60vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl">Términos y Condiciones de Uso</DialogTitle>
          <p className="text-sm text-muted-foreground">ProfessionalsApp</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          <div className="space-y-6 text-sm">
            {/* Table of Contents */}
            <nav className="bg-muted p-4 rounded-lg">
              <ul className="space-y-2">
                <li><a href="#tos-intro" className="text-primary hover:underline">1. Introducción</a></li>
                <li><a href="#tos-def" className="text-primary hover:underline">2. Definiciones</a></li>
                <li><a href="#tos-scope" className="text-primary hover:underline">3. Alcance y Naturaleza de la Plataforma</a></li>
                <li><a href="#tos-reg" className="text-primary hover:underline">4. Registro y Verificación de Usuarios</a></li>
                <li><a href="#tos-obl" className="text-primary hover:underline">5. Obligaciones de los Usuarios</a></li>
                <li><a href="#tos-ip" className="text-primary hover:underline">6. Propiedad Intelectual</a></li>
                <li><a href="#tos-lim" className="text-primary hover:underline">7. Limitación de Responsabilidad y Exenciones</a></li>
                <li><a href="#tos-priv" className="text-primary hover:underline">8. Protección de Datos y Privacidad</a></li>
                <li><a href="#tos-cook" className="text-primary hover:underline">9. Uso de Cookies y Tecnologías Similares</a></li>
                <li><a href="#tos-iap" className="text-primary hover:underline">10. In-App Purchases y Suscripciones Automatizadas</a></li>
                <li><a href="#tos-eval" className="text-primary hover:underline">11. Evaluaciones y Resolución de Conflictos</a></li>
                <li><a href="#tos-mod" className="text-primary hover:underline">12. Modificaciones de los Términos</a></li>
                <li><a href="#tos-term" className="text-primary hover:underline">13. Terminación y Suspensión</a></li>
                <li><a href="#tos-jur" className="text-primary hover:underline">14. Legislación Aplicable y Jurisdicción</a></li>
              </ul>
            </nav>

            <section id="tos-intro">
              <h3 className="text-lg font-semibold mb-2">1. Introducción</h3>
              <p>
                Bienvenido a <strong>ProfessionalsApp</strong> (en adelante, la "Plataforma"). Estos Términos y Condiciones de Uso (en adelante, los "Términos") regulan el acceso, registro y utilización de la Plataforma, propiedad de ProfessionalsApp. Al registrarse, navegar o utilizar cualquier funcionalidad de la Plataforma, usted (cliente o profesional) acepta de forma expresa y sin reservas quedar vinculado por estos Términos.
              </p>
            </section>

            <section id="tos-def">
              <h3 className="text-lg font-semibold mb-2">2. Definiciones</h3>
              <ul className="list-disc ml-6 space-y-1">
                <li><strong>Plataforma</strong>: El sitio web, la aplicación móvil y demás interfaces digitales de ProfessionalsApp.</li>
                <li><strong>Cliente</strong>: Persona natural o jurídica que utiliza la Plataforma para buscar, evaluar y contratar servicios profesionales.</li>
                <li><strong>Profesional</strong>: Persona natural o jurídica que ofrece sus servicios en la Plataforma y ha completado el proceso de registro y verificación.</li>
                <li><strong>Usuario</strong>: Término colectivo para referirse a Clientes y Profesionales.</li>
                <li><strong>Servicios</strong>: Cualquier prestación que un Profesional ofrezca y un Cliente contrate a través de la Plataforma.</li>
                <li><strong>Terceros</strong>: Entidades y personas ajenas a la relación entre Profesionales y Clientes, incluyendo proveedores de pago, agencias de verificación y herramientas analíticas.</li>
              </ul>
            </section>

            <section id="tos-scope">
              <h3 className="text-lg font-semibold mb-2">3. Alcance y Naturaleza de la Plataforma</h3>
              <p className="mb-2">
                <strong>3.1 Función de Intermediación:</strong> La Plataforma actúa exclusivamente como intermediario tecnológico. No forma parte de ningún contrato de prestación de servicios profesional ni supervisa, asume, ni garantiza la ejecución, calidad o idoneidad de los Servicios acordados entre Cliente y Profesional.
              </p>
              <p>
                <strong>3.2 Autonomía Contractual:</strong> El vínculo contractual por el cual un Cliente contrata un Servicio es directo entre Cliente y Profesional; la Plataforma no tiene responsabilidad en la ejecución, pago o disputa de dichos contratos.
              </p>
            </section>

            <section id="tos-reg">
              <h3 className="text-lg font-semibold mb-2">4. Registro y Verificación de Usuarios</h3>
              <p className="mb-2">
                <strong>4.1 Registro de Clientes:</strong> Para acceder, el Cliente debe registrarse con datos veraces y actualizados (nombre, correo, teléfono, país).
              </p>
              <p className="mb-2">
                <strong>4.2 Registro de Profesionales:</strong> El Profesional debe aportar información detallada (formación, experiencia, certificaciones, tarifas) y someterse a verificaciones documentales.
              </p>
              <p>
                <strong>4.3 Suspensión de Registro:</strong> ProfessionalsApp se reserva el derecho de rechazar, suspender o cancelar registros que incumplan políticas o presenten inconsistencias.
              </p>
            </section>

            <section id="tos-obl">
              <h3 className="text-lg font-semibold mb-2">5. Obligaciones de los Usuarios</h3>
              <p className="font-semibold mb-1">5.1 Clientes:</p>
              <ul className="list-disc ml-6 space-y-1 mb-2">
                <li>Verificar la idoneidad y credenciales de los Profesionales antes de contratar.</li>
                <li>Cumplir con términos de pago y condiciones acordadas.</li>
                <li>No publicar contenido falso, ofensivo o infringir derechos de terceros.</li>
              </ul>
              <p className="font-semibold mb-1">5.2 Profesionales:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Mantener veraz y actualizada su información de perfil y tarifas.</li>
                <li>Cumplir obligaciones legales, fiscales y profesionales.</li>
                <li>Respetar Normas de Conducta de la Plataforma y notificar incidencias.</li>
              </ul>
            </section>

            <section id="tos-ip">
              <h3 className="text-lg font-semibold mb-2">6. Propiedad Intelectual</h3>
              <p className="mb-2">6.1 Los derechos de autor y de propiedad intelectual de todos los contenidos (logotipos, marcas, textos, código, diseños) pertenecen a ProfessionalsApp o a Terceros licenciantes.</p>
              <p className="mb-2">6.2 Queda prohibido el uso no autorizado, reproducción, adaptación, distribución o comunicación pública de dichos contenidos sin permiso escrito.</p>
              <p>
                6.3 <strong>Prohibición de Propaganda de Terceros:</strong> Está terminantemente prohibido publicar, difundir o insertar cualquier propaganda o anuncios comerciales de terceros no autorizados por ProfessionalsApp. Cualquier violación podrá derivar en la suspensión inmediata de la cuenta del usuario infractor.
              </p>
            </section>

            <section id="tos-lim">
              <h3 className="text-lg font-semibold mb-2">7. Limitación de Responsabilidad y Exenciones</h3>
              <p className="mb-2">
                <strong>7.1 Limitación General:</strong> ProfessionalsApp no se hace responsable de daños directos, indirectos, emergentes, punitivos o consecuentes derivados del uso de la Plataforma o de Servicios contratados entre Usuarios.
              </p>
              <p className="mb-2">
                <strong>7.2 Ausencia de Garantías:</strong> La Plataforma se ofrece "tal cual" y "según disponibilidad", sin garantías de continuidad, ininterrupción o ausencia de errores.
              </p>
              <p>
                <strong>7.3 Fuerza Mayor:</strong> ProfessionalsApp no responderá por retrasos o incumplimientos causados por eventos de fuerza mayor, fallos técnicos o acciones gubernamentales.
              </p>
            </section>

            <section id="tos-priv">
              <h3 className="text-lg font-semibold mb-2">8. Protección de Datos y Privacidad</h3>
              <p className="mb-2">
                <strong>8.1 Política de Privacidad:</strong> El tratamiento de datos personales se regula en nuestra Política de Privacidad.
              </p>
              <p className="mb-2">
                <strong>8.2 Finalidad del Tratamiento:</strong> Podemos procesar datos para análisis internos, optimización de producto y marketing segmentado, aplicando anonimización y cifrado.
              </p>
              <p className="mb-2">
                <strong>8.3 Conservación y Seguridad:</strong> Los datos se conservan según plazos legales y se protegen con medidas técnicas y organizativas (cifrado, control de accesos).
              </p>
              <p>
                <strong>8.4 Derechos ARCO:</strong> Los Usuarios pueden ejercer acceso, rectificación, cancelación y oposición sobre sus datos personales. Se excluyen de este derecho los comentarios y valoraciones publicados por terceros en perfiles ajenos.
              </p>
            </section>

            <section id="tos-cook">
              <h3 className="text-lg font-semibold mb-2">9. Uso de Cookies y Tecnologías Similares</h3>
              <p className="mb-2">
                <strong>9.1 Cookies Esenciales:</strong> Utilizamos cookies necesarias para el funcionamiento de la Plataforma, incluyendo autenticación y preferencias de sesión.
              </p>
              <p className="mb-2">
                <strong>9.2 Analítica y Telemetría:</strong> Empleamos PostHog como herramienta de análisis para comprender cómo los Usuarios interactúan con la Plataforma. Esta tecnología recopila información como: páginas visitadas, duración de sesiones, tipo de dispositivo y navegador, acciones realizadas dentro de la aplicación y datos de rendimiento. Esta información se procesa de forma agregada y nos ayuda a mejorar la experiencia del usuario.
              </p>
              <p className="mb-2">
                <strong>9.3 Procesamiento por Terceros:</strong> Los datos de analítica son procesados por PostHog Inc., un proveedor externo con sede en Estados Unidos, sujeto a sus propias políticas de privacidad y seguridad.
              </p>
              <p>
                <strong>9.4 Desactivación:</strong> Los Usuarios pueden desactivar las cookies de analítica desde la configuración de su navegador o utilizando extensiones de bloqueo. Sin embargo, esto puede limitar ciertas funcionalidades de la Plataforma.
              </p>
            </section>

            <section id="tos-iap">
              <h3 className="text-lg font-semibold mb-2">10. In-App Purchases y Suscripciones Automatizadas</h3>
              <p className="mb-2">
                10.1 <strong>Adquisición de Funcionalidades Premium:</strong> En la app móvil, algunas características avanzadas se compran mediante in-app purchases en App Store y Google Play.
              </p>
              <p className="mb-2">
                10.2 <strong>Renovación Automática:</strong> Las suscripciones in-app se renuevan automáticamente a menos que se desactive la opción:
              </p>
              <ul className="list-disc ml-6 space-y-1 mb-2">
                <li><strong>iOS/App Store:</strong> Ajustes → [su nombre] → Suscripciones, 24 h antes del fin.</li>
                <li><strong>Android/Google Play:</strong> Play Store → Perfil → Pagos y suscripciones → Suscripciones.</li>
              </ul>
              <p>
                10.3 <strong>Cancelaciones y Reembolsos:</strong> La cancelación impide futuras renovaciones. Los reembolsos se gestionan directamente con la tienda correspondiente.
              </p>
            </section>

            <section id="tos-eval">
              <h3 className="text-lg font-semibold mb-2">11. Evaluaciones y Resolución de Conflictos</h3>
              <p className="mb-2">
                11.1 <strong>Comentarios:</strong> Los Usuarios pueden valorarse mutuamente. La Plataforma eliminará contenido ofensivo o fraudulento, pero no verifica opiniones científicamente.
              </p>
              <p>
                11.2 <strong>Resolución de Conflictos:</strong> Si Cliente y Profesional no llegan a acuerdo, ProfessionalsApp se abstiene de intervenir. En casos extremos de conductas inapropiadas o disputas reiteradas, podrá bloquear o suspender ambas cuentas.
              </p>
            </section>

            <section id="tos-mod">
              <h3 className="text-lg font-semibold mb-2">12. Modificaciones de los Términos</h3>
              <p>ProfessionalsApp podrá actualizar estos Términos con 15 días de antelación. El uso continuado tras la fecha de vigencia implica aceptación.</p>
            </section>

            <section id="tos-term">
              <h3 className="text-lg font-semibold mb-2">13. Terminación y Suspensión</h3>
              <p className="mb-2">
                <strong>13.1 Por Incumplimiento:</strong> Se suspenderá o cancelará acceso a Accounts que violen estos Términos o realicen fraudes.
              </p>
              <p>
                <strong>13.2 Voluntaria:</strong> Usuarios pueden solicitar la baja de su cuenta en cualquier momento, siempre que no tengan obligaciones pendientes.
              </p>
            </section>

            <section id="tos-jur">
              <h3 className="text-lg font-semibold mb-2">14. Legislación Aplicable y Jurisdicción</h3>
              <p className="mb-2">Estos Términos se rigen por la ley del domicilio social de ProfessionalsApp. y cualquier disputa se someterá a los tribunales competentes de dicha jurisdicción.</p>
              <p>
                <strong>Fecha de última actualización:</strong> marzo-2026
              </p>
            </section>
          </div>
        </div>

        <div className="flex justify-center pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
